import React, { Component } from 'react'
import { inject, observer } from 'mobx-react';
import { observable } from 'mobx';
import Pagination from 'components/Pagination';
import Footer from '../Footer';
import { stripHtml } from 'helpers/strings';
import { Alert, Intent } from '@blueprintjs/core';
import Toast from 'helpers/toaster';
import qs from 'qs';
import { currencyFormattedAmount } from 'helpers/currency';
import * as DataList from 'components/DataList';
import { shortDate, clockTime } from 'components/Tables/ScheduleList';

// Column and card layouts, shared with the page's loading skeleton.
export const MIN_TABLE_WIDTH = 1350;

export const COLUMNS = [
  { area: 'number', label: 'Work order #', width: '140px', bone: 'short' },
  { area: 'job', label: 'Job', width: '130px', bone: 'short', icon: 'clipboard' },
  { area: 'date', label: 'Created', width: '120px', bone: 'short' },
  { area: 'client', label: 'Customer', width: 'minmax(190px, 1.4fr)', icon: 'map-marker' },
  { area: 'author', label: 'Created by', width: 'minmax(150px, 1fr)', icon: 'person' },
  { area: 'details', label: 'Description', width: 'minmax(180px, 1.5fr)' },
  { area: 'total', label: 'Grand total', width: '110px', bone: 'single' },
  { area: 'status', label: 'Status', width: '110px', bone: 'pill' },
  { area: 'actions', label: '', width: '72px', bone: 'none' },
];

export const CARDS = {
  columns: 'minmax(0, 1fr) auto auto',
  areas: [
    'number status actions',
    'details details details',
    'client client client',
    'job job job',
    'author author author',
    'date total total',
  ],
  footer: ['date', 'total'],
};

const STATUS_TONES = {
  draft: 'grey',
  submitted: 'blue',
  reviewed: 'purple',
  invoiced: 'green',
};

const capitalise = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : '');

const FILTERS = {
  status: 'status',
  event_number: 'job number',
  po_number: 'PO number',
  service_type_id: 'service type',
  created_by_id: 'created by',
  client_id: 'client',
  location_id: 'location',
};

@inject('store', 'workReportStore', 'viewStore', 'userStore', 'clientStore') @observer
class WorkReportsTable extends Component {

  @observable isDeleteDialogVisible = false;
  @observable workReport;

  onPaginationChange(newPage) {
    let {
      viewStore: {
        workReportSearch
      }
    } = this.props;
    this.props.viewStore.workReportSearch = {
      ...workReportSearch,
      page: newPage
    };
    this.props.viewStore.workReports();
  }

  handleDeleteCancel() {
    this.isDeleteDialogVisible = false;
  }

  handleDeleteConfirm() {
    this.isDeleteDialogVisible = false;
    this.props.workReportStore.deleteWorkReport(this.workReport)
      .then(this.onSuccessfulDeleteWorkReport.bind(this))
      .catch(this.onFailedWorkReportDelete.bind(this))
  }

  onSuccessfulDeleteWorkReport(response) {
    Toast.show({ icon: 'thumbs-up', message: 'Work Report deleted successfully!', intent: Intent.SUCCESS })
    this.workReport = null;
    this.isDeleteDialogVisible = false;
  }

  onFailedWorkReportDelete(error) {
    this.isDeleteDialogVisible = false;
    Toast.show({ icon: 'warning-sign', message: 'Unable to delete work report!', intent: Intent.DANGER })
  }

  handleWorkReportClick(event, workReport) {
    switch (event.label) {
      case 'Edit':
        this.props.viewStore.ShowWorkOrder({ uuid: workReport.attributes.id })
        break;
      case 'Delete':
        this.isDeleteDialogVisible = true;
        this.workReport = workReport;
        break;
      default:
    }
  }

  // Keep the date range and the record kind; drop every other filter.
  clearFilters() {
    const { viewStore } = this.props;
    const { workReportSearch } = viewStore;
    const { where } = workReportSearch;
    viewStore.workReportSearch = {
      ...workReportSearch,
      where: {
        created_at: where.created_at,
        kind: where.kind,
        event_number: null,
        grand_total: { gte: null, lte: null },
      },
      deleted_at: false,
      page: 1,
    };
    viewStore.bumpListFilters();
    viewStore.workReports();
  }

  renderEmpty() {
    const { workReportSearch } = this.props.viewStore;
    const where = workReportSearch.where || {};
    const filters = DataList.activeFilters(where, FILTERS);
    if (where.grand_total && (where.grand_total.gte || where.grand_total.lte)) filters.push('grand total');
    if (workReportSearch.deleted_at) filters.push('deleted');
    if (filters.length > 0) {
      return (
        <DataList.Empty
          noun="work orders"
          filters={filters}
          onClearFilters={this.clearFilters.bind(this)}
        />
      );
    }
    return (
      <DataList.Empty
        noun="work orders"
        title="No work orders in this date range"
        message="Work orders created in this period show up here. Try a wider date range above."
      />
    );
  }

  render() {
    let {
      viewStore,
      viewStore: {
        workReportSearch,
        jobTab: {
          section
        }
      },
      workReportStore: {
        workReports: {
          objects,
          meta
        }
      }
    } = this.props;
    if (objects.length === 0) {
      return this.renderEmpty();
    }
    const pager = (
      <Footer>
        <div className="bp3-navbar-group responsive-pagination bp3-align-left">
          <Pagination
            value={meta.page}
            total={meta.total_pages}
            onChange={this.onPaginationChange.bind(this)}
          />
        </div>
      </Footer>
    );
    return (
      <React.Fragment>
        {pager}
        <DataList.List
          columns={COLUMNS}
          cards={CARDS}
          minTableWidth={MIN_TABLE_WIDTH}
          total={meta.total_records || objects.length}
          noun="work orders"
          note={DataList.sortNote(workReportSearch.order)}
        >
          {objects.map((workReport) => {
            const attributes = workReport.attributes;
            const job = attributes.job || {};
            const client = attributes.client || {};
            const createdBy = attributes.created_by || {};
            const deleted = Boolean(attributes.deleted_at);
            const description = attributes.description ? stripHtml(attributes.description).trim() : '';
            const showLink = `/dashboard/work-reports/${attributes.id}/show`;
            return (
              <DataList.Row key={workReport.id}>
                <DataList.Cell area="number">
                  <div className="dl-lines">
                    <DataList.RowLink href={showLink} label={`Work order ${attributes.number}`}>
                      #{attributes.number}
                    </DataList.RowLink>
                    {attributes.invoice_number && (
                      <div className="dl-muted dl-truncate">Invoice {attributes.invoice_number}</div>
                    )}
                  </div>
                </DataList.Cell>
                <DataList.Cell area="job">
                  {job.id ? (
                    <DataList.Lines
                      primary={`Job #${attributes.event_number}`}
                      href={`/dashboard/jobs/${job.id}/${section}`}
                      secondary={job.service_type_name}
                    />
                  ) : (
                    <DataList.Muted>No job</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="date">
                  <DataList.Lines
                    primary={<React.Fragment><DataList.CardOnly>Created </DataList.CardOnly>{shortDate(attributes.created_at)}</React.Fragment>}
                    secondary={clockTime(attributes.created_at)}
                    strong={false}
                    title={shortDate(attributes.created_at)}
                  />
                </DataList.Cell>
                <DataList.Cell area="client">
                  <DataList.Lines
                    primary={client.name}
                    href={client.id
                      ? `/dashboard/clients/${client.id}?${qs.stringify(viewStore.clientShowSearch, { encode: false })}`
                      : undefined}
                    secondary={attributes.location && attributes.location.full_address}
                  />
                </DataList.Cell>
                <DataList.Cell area="author">
                  <DataList.Lines
                    primary={createdBy.full_name}
                    href={createdBy.id ? `/dashboard/users/${createdBy.id}/show?resource=vacations` : undefined}
                    secondary={createdBy.email}
                  />
                </DataList.Cell>
                <DataList.Cell area="details">
                  <DataList.Lines
                    primary={description || <DataList.Muted>No description</DataList.Muted>}
                    title={description}
                    strong={false}
                    secondary={attributes.po_number ? `PO ${attributes.po_number}` : null}
                  />
                </DataList.Cell>
                <DataList.Cell area="total">
                  <div className="dl-lines__primary">
                    <DataList.CardOnly>Total </DataList.CardOnly>
                    {currencyFormattedAmount(attributes.grand_total)}
                  </div>
                </DataList.Cell>
                <DataList.Cell area="status">
                  {deleted ? (
                    <DataList.Pill tone="red">Deleted</DataList.Pill>
                  ) : (
                    <DataList.Pill tone={STATUS_TONES[attributes.status] || 'grey'}>
                      {capitalise(attributes.status)}
                    </DataList.Pill>
                  )}
                </DataList.Cell>
                <DataList.Cell area="actions">
                  <DataList.Actions
                    items={[
                      { icon: 'edit', label: 'Edit work order', href: showLink },
                      {
                        icon: 'trash',
                        label: 'Delete work order',
                        intent: 'danger',
                        onClick: () => this.handleWorkReportClick({ label: 'Delete' }, workReport),
                      },
                    ]}
                  />
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Delete"
          icon="trash"
          intent={Intent.DANGER}
          isOpen={this.isDeleteDialogVisible}
          onCancel={this.handleDeleteCancel.bind(this)}
          onConfirm={this.handleDeleteConfirm.bind(this)}
        >
          <p>
            All associated data of this work order (such as line items, equipments, images, etc) will also be deleted. Are you sure you want to do this?
          </p>
        </Alert>
        {pager}
      </React.Fragment>
    )
  }
}

export default WorkReportsTable;
